import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Alert, EmptyState, ScrollTopButton, Skeleton } from '@indipia/ui-core';
import { classifyLinkViewError, ShareLinkViewErrorScreen } from '@indipia/shared-features';
import {
  customerHomeZipUrl,
  fetchCustomerHome,
  fetchCustomerHomePreview,
  recordCustomerHomeVisit,
  type CustomerHomeData,
  type ReportCard,
} from '../api/customerHome';
import { DashboardTabs, type DashboardTabItem, type DashboardTabKey } from '../customer-home/DashboardTabs';
import { NewsBanner } from '../customer-home/NewsBanner';
import { PhotoTab, groupPhotos } from '../customer-home/PhotoTab';
import { ReportTab } from '../customer-home/ReportTab';
import { ScheduleTab } from '../customer-home/ScheduleTab';
import { PhotoViewer, type ViewerPhoto } from '../customer-home/PhotoViewer';
import { PreviewContext } from '../customer-home/PreviewContext';
import { buildZipFilename, savePhoto, triggerAnchorDownload } from '../customer-home/download';
import { fmtDate, fmtDateDow, fmtDateDowTime, fmtMd, fmtMdTime } from '../customer-home/format';

const TAB_ORDER: DashboardTabKey[] = ['PHOTO', 'SCHEDULE', 'REPORT'];
const TAB_LABEL: Record<DashboardTabKey, string> = { PHOTO: '공유사진', SCHEDULE: '공사스케줄', REPORT: '현장리포트' };
// 배너 문구 순서(고객에게 중요한 순) · 단위
const BANNER_ORDER: { key: DashboardTabKey; label: string; unit: string }[] = [
  { key: 'REPORT', label: '현장리포트', unit: '건' },
  { key: 'PHOTO', label: '공유사진', unit: '장' },
  { key: 'SCHEDULE', label: '일정 변경', unit: '건' },
];

/** 일시 오류(네트워크·5xx·SHARE_LINK_TEMPORARY)만 재시도 대상 — 접속 시간 초과·만료·회수·무효는 다시 시도해도 소용없어 안내만 한다. */
function isRetryableViewError(err: unknown): boolean {
  return classifyLinkViewError(err) === 'TEMPORARY';
}

type ViewerState = { group: 'photo' } | { group: 'report'; crItemId: number } | null;

function tabSummary(data: CustomerHomeData, key: DashboardTabKey) {
  return key === 'PHOTO' ? data.photo : key === 'SCHEDULE' ? data.schedule : data.worklog;
}

function PinIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  );
}

interface CustomerHomePageProps {
  /**
   * 주면 발송자 미리보기 — 로그인한 사업장 회원이 이 shareId(발송 이력의 수신자 행)의 고객 화면을 부작용 없이 본다.
   * 방문 기록·읽음 처리·사진 저장을 하지 않고 상단에 미리보기 배너를 띄운다. 생략하면 고객 진입(공유쿠키).
   */
  previewShareId?: number;
  /** 미리보기에서 과거 발송 건을 지정하는 발송 이력 번호(선택). */
  previewHistoryId?: number;
}

/**
 * 고객대시보드(공용) — 설계서 §7.6. 고객이 알림톡 버튼으로 여는 무인증(공유쿠키) 모바일웹.
 * 탭(공유사진·공사스케줄·현장리포트)은 사장님이 실제로 발송한 항목만 노출하고, 직전 방문 이후 새 소식을
 * 탭 배지·상단 배너·NEW 표시로 알린다. 첫 방문(직전 방문 없음)은 "새 소식"이 아니라 "처음 보는 화면"이라
 * 배지·배너·NEW를 모두 숨긴다. 직전 방문 시각은 서버가 고객×현장 단위로 1개만 저장한다.
 */
export function CustomerHomePage({ previewShareId, previewHistoryId }: CustomerHomePageProps = {}) {
  const preview = previewShareId !== undefined;
  const { data, error, isPending, isError, refetch } = useQuery({
    queryKey: ['customer-home', preview ? 'preview' : 'me', previewShareId ?? null, previewHistoryId ?? null],
    queryFn: ({ signal }) => (preview ? fetchCustomerHomePreview(previewShareId, signal, previewHistoryId) : fetchCustomerHome(signal)),
    retry: (failureCount, err) => isRetryableViewError(err) && failureCount < 1,
    staleTime: Infinity, // 방문 중 재조회하면 직전 방문 기준이 바뀌어 NEW 표시가 사라진다
    refetchOnWindowFocus: false,
  });

  if (isPending) return <DashboardSkeleton />;
  if (isError) {
    const kind = classifyLinkViewError(error);
    // 접속 시간 초과(401·403) — 기존 문구 유지. 미리보기는 발송자 로그인 안내.
    if (kind === 'SESSION') {
      return preview ? (
        <Frame>
          <EmptyState title="미리보기를 열 수 없습니다" description="로그인 상태를 확인한 뒤 다시 열어 주세요." />
        </Frame>
      ) : (
        <ShareLinkViewErrorScreen
          kind="SESSION"
          title="링크가 만료되었습니다"
          description="알림톡의 [고객대시보드 확인하기] 버튼을 다시 눌러 열어 주세요."
        />
      );
    }
    return <ShareLinkViewErrorScreen kind={kind} onRetry={() => void refetch()} />;
  }
  if (!data.found) {
    // 사유(reason)가 있으면 공용 오류 화면 — 미리보기는 발송자 화면이라 기존 안내 유지. 일시 오류만 다시 시도.
    if (!preview && data.reason) {
      return <ShareLinkViewErrorScreen kind={data.reason} onRetry={data.reason === 'TEMPORARY' ? () => void refetch() : undefined} />;
    }
    return (
      <Frame>
        {preview ? (
          <EmptyState title="미리보기를 열 수 없습니다" description="공유가 없거나 우리 사업장의 현장이 아닙니다." />
        ) : (
          <EmptyState title="공유 링크를 열 수 없습니다" description="링크가 만료되었거나 올바르지 않습니다. 사장님께 다시 요청해 주세요." />
        )}
      </Frame>
    );
  }
  return (
    <PreviewContext.Provider value={preview}>
      <Dashboard data={data} preview={preview} />
    </PreviewContext.Provider>
  );
}

function Frame({ children }: { children: React.ReactNode }) {
  return <div className="mx-auto flex min-h-screen w-full max-w-md flex-col justify-center bg-surface p-05">{children}</div>;
}

function DashboardSkeleton() {
  return (
    <div className="mx-auto min-h-screen w-full max-w-md bg-surface" aria-busy="true">
      <div className="border-b border-border bg-surface-raised p-05">
        <Skeleton width="40%" height="var(--spacing-05)" />
        <Skeleton width="70%" height="var(--spacing-04)" className="mt-03" />
      </div>
      <div className="flex flex-col gap-04 p-05">
        {[0, 1, 2].map((i) => (
          <div key={i} className="rounded-xl border border-border bg-surface-raised p-05">
            <Skeleton width="38%" height="var(--spacing-04)" />
            <Skeleton width="82%" height="var(--spacing-04)" className="mt-03" />
            <Skeleton width="60%" height="var(--spacing-04)" className="mt-03" />
          </div>
        ))}
      </div>
    </div>
  );
}

function Dashboard({ data, preview }: { data: CustomerHomeData; preview: boolean }) {
  // 노출 탭 — 발송 이력이 있는 탭만(빈 탭을 보여주지 않는다)
  const visibleKeys = useMemo(() => TAB_ORDER.filter((k) => tabSummary(data, k)?.hasContent), [data]);

  // 진입 탭 — ① 서버가 지정한 탭(알림톡 진입 등) ② 새 소식(직전 방문 이후 미열람)이 있는 탭(BANNER_ORDER 순) ③ 일정 탭 ④ 첫 탭.
  // 숨겨진(내용 없는) 탭 지정은 다음 순위로 넘어간다. 탭 구성은 그대로이고 처음 열리는 탭만 정한다(스케줄 공유 결정 51 D-2).
  const [active, setActive] = useState<DashboardTabKey | null>(() => {
    if (data.entryTab && visibleKeys.includes(data.entryTab)) return data.entryTab;
    if (data.lastVisitedDtm != null) {
      const withNews = BANNER_ORDER.find(({ key }) => visibleKeys.includes(key) && (tabSummary(data, key)?.unread ?? 0) > 0);
      if (withNews) return withNews.key;
    }
    if (visibleKeys.includes('SCHEDULE')) return 'SCHEDULE';
    return visibleKeys[0] ?? null;
  });
  const [bannerHidden, setBannerHidden] = useState(false);
  const [viewer, setViewer] = useState<{ state: ViewerState; index: number }>({ state: null, index: 0 });

  // 방문 기록 — 진입 시 판정에 쓴 직전 방문시각을 이번 진입 시각으로 갱신(설계서 §7.6.4, 이탈 감지·비콘 없음).
  // 화면이 이미 직전 방문시각(data.lastVisitedDtm)을 들고 있어 이 호출이 이번 방문의 NEW 표시를 지우지 않는다.
  // 미리보기는 고객이 아니므로 기록하지 않는다(서버에도 미리보기용 방문 기록 경로가 없다).
  const visitedRef = useRef(false);
  useEffect(() => {
    if (preview || visitedRef.current) return;
    visitedRef.current = true;
    void recordCustomerHomeVisit().catch(() => undefined);
  }, [preview]);

  // 새 소식 표시(탭 배지 · 상단 배너 · NEW)는 이번 방문 동안 유지한다 — 탭을 열어 봐도 사라지지 않는다.
  // 방문 기록은 진입 때 이미 갱신되므로, 다시 들어오면 직전 방문 시각이 새로 잡혀 새 소식이 없는 상태로 열린다.
  // 상단 배너는 사용자가 [닫기]를 눌러야만 접힌다.

  const hasVisited = data.lastVisitedDtm != null;
  const tabs: DashboardTabItem[] = visibleKeys.map((key) => ({
    key,
    label: TAB_LABEL[key],
    unread: hasVisited ? (tabSummary(data, key)?.unread ?? 0) : 0,
  }));

  // 새 소식 배너 — 직전 방문이 있고 새 소식이 1건 이상일 때만. 이번 방문 동안 유지하고 [닫기]로만 접는다.
  const bannerParts = BANNER_ORDER.flatMap(({ key, label, unit }) => {
    const unread = hasVisited ? (tabSummary(data, key)?.unread ?? 0) : 0;
    return unread > 0 ? [`${label} ${unread}${unit}`] : [];
  });
  const showBanner = hasVisited && bannerParts.length > 0 && !bannerHidden;

  // 탭 데이터
  const photos = data.photo?.content.photos ?? [];
  const photoShares = data.photo?.content.shares ?? [];
  const reportCards = data.worklog?.content.reportCards ?? [];
  const photoOrdered = useMemo(
    () => groupPhotos(photos, photoShares, data.lastVisitedDtm).ordered,
    [photos, photoShares, data.lastVisitedDtm],
  );

  // 알림톡으로 도착한 리포트 카드 — 여러 건이 함께 발송됐으면 가장 최근 발송분(DEV_RULES). 토큰의 resourceRef는 카드가 아니라 리포트 단위다.
  const entryCardId = useMemo(() => {
    if (data.entryTab !== 'REPORT' || reportCards.length === 0) return null;
    return reportCards.reduce((a, b) => (a.sentDtm >= b.sentDtm ? a : b)).crItemId;
  }, [data.entryTab, reportCards]);

  // 사진 뷰어 — 공유사진 탭은 탭 전체를 연속으로, 현장리포트는 해당 카드 안에서만 넘긴다.
  const viewerPhotos: ViewerPhoto[] = useMemo(() => {
    const vs = viewer.state;
    if (!vs) return [];
    if (vs.group === 'photo') {
      return photoOrdered.map((p) => ({
        thumb: p.thumbUrl,
        src: p.originalUrl ?? p.thumbUrl,
        caption: `${fmtDateDow(p.sharedDtm)} 공유사진`,
      }));
    }
    const card = reportCards.find((c) => c.crItemId === vs.crItemId);
    if (!card) return [];
    return card.photos
      .slice()
      .sort((a, b) => a.photoSeq - b.photoSeq)
      .map((p) => ({
        thumb: p.url,
        src: p.url,
        caption: `${fmtDateDow(card.workDate)} 현장리포트 · ${card.workTypeNames.join(', ')}`,
        sub: `${fmtMdTime(card.sentDtm)} 발송`,
      }));
  }, [viewer.state, photoOrdered, reportCards]);

  // 사진 저장 — 서버 zip(앵커 이동)과 개별 저장. 미리보기는 로그인 토큰만 있어 앵커 이동으로는 자격을 못 실으므로 막는다.
  const downloadZip = useCallback(
    (bundleIds?: string[]) => triggerAnchorDownload(customerHomeZipUrl(bundleIds), buildZipFilename(data.siteName)),
    [data.siteName],
  );
  const downloadViewerPhoto = useCallback(
    (index: number) => {
      const p = photoOrdered[index];
      const url = p?.originalUrl ?? p?.thumbUrl;
      if (p && url) void savePhoto(url, `photo-${p.photoId}.jpg`);
    },
    [photoOrdered],
  );

  const openCardPhoto = useCallback((card: ReportCard, photoIndex: number) => {
    setViewer({ state: { group: 'report', crItemId: card.crItemId }, index: photoIndex });
  }, []);

  const period = data.schedule?.content.startDate && data.schedule.content.endDate
    ? `${fmtDate(data.schedule.content.startDate)}~${fmtMd(data.schedule.content.endDate)}`
    : null;

  if (!active) {
    return (
      <Frame>
        <EmptyState title="아직 공유된 내용이 없습니다" description="사장님이 사진·일정·리포트를 공유하면 여기에서 확인할 수 있습니다." />
      </Frame>
    );
  }

  return (
    <div className="mx-auto flex min-h-screen w-full max-w-md flex-col bg-surface">
      <div className="sticky top-0 z-sticky">
        {preview && (
          <Alert kind="info" title="미리보기" className="rounded-none border-x-0 border-t-0">
            <span className="text-caption">고객에게 보이는 화면입니다. 열람 기록·알림은 남지 않으며 사진 저장은 고객 화면에서만 됩니다.</span>
          </Alert>
        )}
        <header className="border-b border-border bg-surface-raised px-05 pb-04 pt-04">
          <h1 className="mb-03 text-body-md font-bold text-text">우리집 공사 소식</h1>
          <div className="flex flex-wrap items-center gap-03 text-caption font-bold text-text-secondary">
            <PinIcon />
            <span>{data.siteName ?? data.siteAddress ?? '현장'}</span>
            {period && (
              <>
                <span aria-hidden="true" className="font-normal text-border">
                  |
                </span>
                <span>{period}</span>
              </>
            )}
          </div>
          {data.linkExpiresDtm && (
            <div className="mt-02 text-caption text-text-secondary">링크 유효기간 {fmtDateDowTime(data.linkExpiresDtm)}까지</div>
          )}
        </header>
        {showBanner && data.lastVisitedDtm && (
          <NewsBanner lastVisitedDtm={data.lastVisitedDtm} parts={bannerParts} onClose={() => setBannerHidden(true)} />
        )}
        <DashboardTabs tabs={tabs} active={active} onChange={setActive} />
      </div>

      <main role="tabpanel" className="flex-1 p-05">
        {active === 'PHOTO' && (
          <PhotoTab
            photos={photos}
            shares={photoShares}
            lastVisitedDtm={data.lastVisitedDtm}
            onOpen={(index) => setViewer({ state: { group: 'photo' }, index })}
            onDownloadZip={preview ? undefined : downloadZip}
          />
        )}
        {active === 'SCHEDULE' && data.schedule && <ScheduleTab content={data.schedule.content} />}
        {active === 'REPORT' && (
          <ReportTab
            cards={reportCards}
            lastVisitedDtm={data.lastVisitedDtm}
            highlightCrItemId={entryCardId}
            onOpenPhoto={openCardPhoto}
          />
        )}
      </main>

      {/* 스크롤 맨 위로 (§15.55) — Shell 밖 단일 컬럼이라 스크롤 소유자가 window 다.
          사진 탭이 길어져도 헤더(sticky)까지 한 번에 돌아온다. */}
      <ScrollTopButton />

      {viewer.state && viewerPhotos.length > 0 && (
        <PhotoViewer
          photos={viewerPhotos}
          index={viewer.index}
          onIndexChange={(index) => setViewer((v) => ({ ...v, index }))}
          onClose={() => setViewer({ state: null, index: 0 })}
          onDownload={!preview && viewer.state.group === 'photo' ? downloadViewerPhoto : undefined}
        />
      )}
    </div>
  );
}
